import { createContext, useContext, FC, ReactNode, useState } from 'react';
import { ConfirmationModal } from '../components/Modal/ConfirmationModal';

interface ConfirmationContextProps {
  confirm: (options: ConfirmationOptions) => Promise<boolean>;
}

interface ConfirmationOptions {
  title?: string;
  message?: string;
}

interface ConfirmationProviderProps {
  children: ReactNode;
}

const ConfirmationContext = createContext<ConfirmationContextProps | undefined>(undefined);

export function useConfirmation() {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error('useConfirmation must be used within a ConfirmationProvider');
  }
  return context;
}

export const ConfirmationProvider: FC<ConfirmationProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmationOptions>({});
  const [resolve, setResolve] = useState<(value: boolean) => void>(() => { return; });

  const confirm = (options: ConfirmationOptions) => {
    setOptions(options);
    setOpen(true);
    return new Promise<boolean>((res) => {
      setResolve(() => res);
    });
  };

  const handleConfirm = () => {
    setOpen(false);
    resolve(true);
  };

  const handleCancel = () => {
    setOpen(false);
    resolve(false);
  };

  return (
    <ConfirmationContext.Provider value={{ confirm }}>
      {children}
      <ConfirmationModal
        open={open}
        title={options.title}
        message={options.message}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmationContext.Provider>
  );
};
